import type { FundSheetAnalysisPageSearchQueryStates } from '../_hooks/search-query-states';

const KS_SEARCH_HISTORY_STORAGE_KEY = 'ks-analysis-search-history';

/**
 * 履歴「戻る」で復元する対象＝ドリルダウン state のみ (RS-4493)。
 * cascader は内部サイト（QueryStore 経由）、organizationIds は公開サイト(PS)のドリルダウン現在地。
 */
type KsDrillDownHistoryState = Pick<
  FundSheetAnalysisPageSearchQueryStates,
  'cascader' | 'organizationIds'
>;

/**
 * URL クエリ全体からドリルダウン state のみを抽出する。
 * 履歴 snapshot に年度タブ(chartYear)・列ソート(order)・ページング(page)等を含めると、
 * 「戻る」がドリルダウンではなくそれらを巻き戻してしまうため、対象キーを限定する (RS-4493)。
 */
export const pickKsDrillDownState = (
  states: KsDrillDownHistoryState,
): KsDrillDownHistoryState => ({
  cascader: states.cascader,
  organizationIds: states.organizationIds,
});

/**
 * sessionStorage から読み出した entry をドリルダウン state に正規化する。
 * 旧形式（URL クエリ全量 snapshot）や不正な値（非オブジェクト・型不一致）が
 * 残っていてもクラッシュ・undefined 混入を起こさないよう、型を検証して null に寄せる。
 */
const toKsDrillDownState = (entry: unknown): KsDrillDownHistoryState => {
  if (!entry || typeof entry !== 'object') {
    return { cascader: null, organizationIds: null };
  }
  const { cascader, organizationIds } = entry as Record<string, unknown>;
  return {
    cascader: typeof cascader === 'string' ? cascader : null,
    organizationIds:
      Array.isArray(organizationIds) &&
      organizationIds.every((id) => typeof id === 'string')
        ? organizationIds
        : null,
  };
};

/**
 * 履歴を sessionStorage から読み出す。破損 JSON・配列以外の値は空履歴として扱い、
 * 各 entry は読み出し時点でドリルダウン state に正規化する（旧形式との互換, RS-4493）。
 */
export const getKsAnalysisSearchHistory = (): KsDrillDownHistoryState[] => {
  const storedHistory = sessionStorage.getItem(KS_SEARCH_HISTORY_STORAGE_KEY);
  if (!storedHistory) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(storedHistory);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map(toKsDrillDownState);
  } catch {
    return [];
  }
};

export const setKsAnalysisSearchHistory = (
  history: KsDrillDownHistoryState[],
) => {
  sessionStorage.setItem(
    KS_SEARCH_HISTORY_STORAGE_KEY,
    JSON.stringify(history),
  );
};

export const removeKsAnalysisSearchHistory = () => {
  sessionStorage.removeItem(KS_SEARCH_HISTORY_STORAGE_KEY);
};
